<script context="module" lang="ts">
    /** Camera Device interface. */
    interface CameraDevice {
        id: string;
        label: string;
    }

    /**
     * Code formats supported by this library.
     */
    enum Html5QrcodeSupportedFormats {
        QR_CODE = 0,
        AZTEC,
        CODABAR,
        CODE_39,
        CODE_93,
        CODE_128,
        DATA_MATRIX,
        MAXICODE,
        ITF,
        EAN_13,
        EAN_8,
        PDF_417,
        RSS_14,
        RSS_EXPANDED,
        UPC_A,
        UPC_E,
        UPC_EAN_EXTENSION,
    }

    /** Format of detected code. */
    class QrcodeResultFormat {
        public readonly format: Html5QrcodeSupportedFormats;
        public readonly formatName: string;
    }

    /** Detailed scan result. */
    interface QrcodeResult {
        /** Decoded text. */
        text: string;

        /** Format that was successfully scanned. */
        format?: QrcodeResultFormat;
    }

    /** QrCode result object. */
    interface Html5QrcodeResult {
        decodedText: string;
        result: QrcodeResult;
    }

    /** Type for a callback for a successful code scan. */
    type QrcodeSuccessCallback = (
        decodedText: string,
        result: Html5QrcodeResult
    ) => void;

    /** Type for a callback for failure during code scan. */
    type QrcodeErrorCallback = (errorMessage: string, error: any) => void;

    /**
     * Interface for configuring {@class Html5Qrcode} class instance.
     */
    interface Html5QrcodeConfigs {
        /**
         * Array of formats to support of type {@type Html5QrcodeSupportedFormats}.
         */
        formatsToSupport: Array<Html5QrcodeSupportedFormats> | undefined;
    }

    /** Configuration for creating {@class Html5Qrcode}. */
    interface Html5QrcodeFullConfig extends Html5QrcodeConfigs {
        /**
         * If true, all logs would be printed to console. False by default.
         */
        verbose: boolean | undefined;
    }

    interface Html5QrcodeCameraScanConfig {
        /**
         * Optional, Expected framerate of QR code scanning. example { fps: 2 } means the
         * scanning would be done every 500 ms.
         */
        fps: number | undefined;

        /**
         * Optional, width of scanning region box, this should be smaller than the
         * width and height of the full region.
         * This would make the scanner look like this:
         *          ----------------------
         *          |********************|
         *          |******,,,,,,,,,*****|      <--- shaded region
         *          |******|       |*****|      <--- non shaded region would be
         *          |******|       |*****|          used for QR code scanning.
         *          |******|_______|*****|
         *          |********************|
         *          |********************|
         *          ----------------------
         */
        qrbox?: number | undefined;

        /**
         * Optional, Desired aspect ratio for the video feed. Ideal aspect ratios
         * are 4:3 or 16:9. Passing very wrong aspect ratio could lead to video feed
         * not showing up.
         */
        aspectRatio?: number | undefined;

        /**
         * Optional, if {@code true} flipped QR Code won't be scanned. Only use this
         * if you are sure the camera cannot give mirrored feed if you are facing
         * performance constraints.
         */
        disableFlip?: boolean | undefined;

        /*
         * Optional, @beta(this config is not well supported yet).
         *
         * Important: When passed this will override other parameters like
         * 'cameraIdOrConfig' or configurations like 'aspectRatio'.
         * 'videoConstraints' should be of type {@code MediaTrackConstraints} as
         * defined in
         * https://developer.mozilla.org/en-US/docs/Web/API/MediaTrackConstraints
         * and is used to specify a variety of video or camera controls like:
         * aspectRatio, facingMode, frameRate, etc.
         */
        videoConstraints?: MediaTrackConstraints | undefined;
    }

    /**
     * Interface for controlling different aspects of {@class Html5QrcodeScanner}.
     */
    interface Html5QrcodeScannerConfig
        extends Html5QrcodeCameraScanConfig,
            Html5QrcodeConfigs {}

    declare class Html5Qrcode {
        /**
         * Returns a Promise with a list of all cameras supported by the device.
         */
        static getCameras(): Promise<Array<CameraDevice>>; // Returns a Promise

        /**
         * Initialize QR Code scanner.
         *
         * @param elementId - Id of the HTML element.
         * @param verbose - optional configuration object
         */
        constructor(
            elementId: string,
            config: Html5QrcodeFullConfig | undefined
        );

        /**
         * Start scanning QR codes or barcodes for a given camera.
         *
         * @param cameraIdOrConfig Identifier of the camera, it can either be the
         *  camera id retrieved from {@code Html5Qrcode#getCameras()} method or
         *  object with facing mode constraint.
         * @param configuration Extra configurations to tune the code scanner.
         * @param qrCodeSuccessCallback Callback called when an instance of a QR
         * code or any other supported bar code is found.
         * @param qrCodeErrorCallback Callback called in cases where no instance of
         * QR code or any other supported bar code is found.
         */
        start(
            cameraIdOrConfig: any,
            configuration: Html5QrcodeCameraScanConfig | undefined,
            qrCodeSuccessCallback: QrcodeSuccessCallback | undefined,
            qrCodeErrorCallback: QrcodeErrorCallback | undefined
        ): Promise<null>;

        /**
         * Stops streaming QR Code video and scanning.
         */
        stop(): Promise<void>;

        /**
         * Scans an Image File for QR Code.
         *
         * This feature is mutually exclusive to camera-based scanning, you should
         * call stop() if the camera-based scanning was ongoing.
         *
         * @param imageFile a local file with Image content.
         * @param showImage if true, the Image will be rendered on given element.
         *
         * @returns Promise with decoded QR code string on success.
         */
        scanFile(
            imageFile: File,
            /* default=true */ showImage: boolean | undefined
        ): Promise<string>;

        /**
         * Clears the existing canvas.
         *
         * Note: in case of ongoing web-cam based scan, it needs to be explicitly
         * closed before calling this method, else it will throw an exception.
         */
        clear(): void; // Returns void
    }
</script>

<script lang="ts">
    import { onMount } from "svelte";
    import { mediaQuery } from "../../../../../store/stores";

    export let validateQrResult;
    let qrScanner: Html5Qrcode;
    let activeQrScan: boolean;
    let cameraDevices: Promise<CameraDevice[]>;
    let activeDevice: CameraDevice;

    let detectCameraDevices = async () => {
        return await Html5Qrcode.getCameras().then((devices) => {
            if (devices && devices.length) {
                return devices;
            }
        });
    };

    function deactivateScanner() {
        qrScanner.stop();
        qrScanner.clear();
        activeQrScan = false;
    }

    function activateScanner(cameraInt = 0) {
        console.log("activateScanner", cameraInt);
        activeQrScan = true;
        Html5Qrcode.getCameras()
            .then((devices) => {
                /**
                 * devices would be an array of objects of type:
                 * { id: "id", label: "label" }
                 *
                 *
                 */
                if (devices && devices.length) {
                    activeDevice = devices[cameraInt];
                    var cameraId = activeDevice.id;

                    // alert(devices.map((device) => device.label).join(" "));
                    // .. use this to start scanning.
                    qrScanner = new Html5Qrcode(
                        /* element id */ "qrScanner",
                        undefined
                    );
                    qrScanner
                        .start(
                            cameraId,
                            {
                                fps: 10, // Optional, frame per seconds for qr code scanning
                                qrbox: 250, // Optional, if you want bounded box UI
                            },
                            (decodedText, decodedResult) => {
                                console.log(validateQrResult);
                                console.log(decodedText);
                                validateQrResult(decodedText);
                                deactivateScanner();
                                // do something when code is read
                            },
                            (errorMessage) => {
                                // parse error, ignore it.
                            }
                        )
                        .catch((err) => {
                            // Start failed, handle it.
                            alert(err);
                        });
                }
            })
            .catch((err) => {
                // handle err
                alert(err);
            });
    }

    const switchCamera = (camera: any) => {
        qrScanner.stop();
        console.log(camera.id);
        activeDevice = camera.id;
        qrScanner
            .start(
                camera.id,
                {
                    fps: 10, // Optional, frame per seconds for qr code scanning
                    qrbox: 250, // Optional, if you want bounded box UI
                },
                (decodedText, decodedResult) => {
                    console.log(validateQrResult);
                    console.log(decodedText);
                    validateQrResult(decodedText);
                    deactivateScanner();
                    // do something when code is read
                },
                (errorMessage) => {
                    // parse error, ignore it.
                }
            )
            .catch((err) => {
                // Start failed, handle it.
                alert(err);
            });
    };

    onMount(() => {
        cameraDevices = detectCameraDevices();
    });
</script>

<button
    type="submit"
    on:click={() => {
        activateScanner();
    }}>Scan Code</button
>
{#if activeQrScan}
    <div class="modal {activeQrScan ? 'is-active' : null} qr-container">
        <div class="modal-background" on:click={() => deactivateScanner()}>
            <div
                class="modal-content"
                style={$mediaQuery.mql.matches ? "margin-left: 0;" : ""}
            >
                <div id="qrScanner" />
                {#await cameraDevices then devices}
                    <!-- {#if devices.length > 1} -->
                    <button
                        class="is-large"
                        on:click|stopPropagation={() =>
                            switchCamera(
                                devices.filter(
                                    (device) =>
                                        device.id !== activeDevice.id ||
                                        devices.length === 1
                                )[0]
                            )}
                    >
                        <span class="icon">
                            <i class="fas fa-sync" />
                        </span>
                    </button>
                    <!-- {/if} -->
                {:catch error}
                    <p style="color: red">{error.message}</p>
                {/await}
            </div>
        </div>
        <button
            class="modal-close is-large"
            aria-label="close"
            on:click={() => deactivateScanner()}
        />
    </div>
{/if}

<style>
    #qrScanner {
        width: 100%;
        max-width: 800px;
    }

    .modal-content {
        margin-top: 5rem;
    }
</style>
